import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { FormikProps, Formik, Form, Field } from "formik";
import { Col, Row } from "react-bootstrap";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { get, map, pickBy } from "lodash";
import { InputCheckboxField } from "../../../../../components/Shared/InputCheckboxField";
import * as XLSX from "xlsx";
import qs from "qs";
import { request } from "../../../../../utils/Request";
import { utcDateToLocalDate } from "../../../../../utils/Helper";

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    visibility: any;
    toastify: any;
    onClose?: any;
}

type TInputForm = {
    consider_all_pages: boolean;
    title: boolean;
    slug: boolean;
    created_at: boolean;
};

const DEFAULT_FORM_STATE = {
    consider_all_pages: true,
    title: true,
    slug: true,
    created_at: true,
};

function ProviderExcel(props: IFormProps) {
    const { t, visibility } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<any>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);

    const onSubmit = async (payloads: TInputForm): Promise<void> => {
        const _params: any = props.moduleState;
        const _payloads: any = payloads;

        if (payloads.consider_all_pages) {
            delete _params.page;
            delete _params.per_page;
        }

        try {
            const response = await request({
                url: "/suppliers/settings/providers",
                params: _params,
                paramsSerializer: {
                    serialize: (params: any) => qs.stringify(params),
                },
            });

            delete _payloads.consider_all_pages;
            const columns = pickBy(_payloads, (item: boolean) => item);
            const title: any = getTitles();
            const data: any = [];
            map(response.data.data, (value: any) => {
                const row: any = {
                    title: value.title,
                    slug: value.slug,
                    created_at: utcDateToLocalDate(value.created_at),
                };

                const _row: any = {};
                map(columns, (_, key: string) => (_row[key] = row[key]));
                const arrayOfRow = Object.keys(_row).map((key) => _row[key]);
                data.push(arrayOfRow);
            });

            const _title = map(columns, (_, key: string) => (title[key] = title[key]));
            const excel = XLSX.utils.book_new();
            const sheet = XLSX.utils.aoa_to_sheet([_title, ...data]);
            XLSX.utils.book_append_sheet(excel, sheet, t("providers"));
            XLSX.writeFile(excel, "providers.xlsx");
        } catch (e: any) {
            props.toastify(t("server error"), "error");
        }
    };

    const onClose = () => {
        setPanelState(false);

        if (props.onClose) {
            props.onClose();
        }
    };

    const getTitles = () => {
        return {
            title: t("title"),
            slug: t("slug"),
            created_at: t("created at"),
        };
    };

    useEffect(() => {
        if (visibility) {
            setPanelState(visibility);
            setInitFormState({ ...DEFAULT_FORM_STATE, ...props.moduleState.filters ?? [] });
        }
    }, [visibility]);

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0" }>{ t("export") } { t("providers") }</h5>
                    <p>{ t("select columns to be exported.") }</p>
                </div>
            }
            visible={ panelState }
            position="left"
            onHide={ onClose }
            blockScroll={ true }
            style={ { width: "400px" } }
            closeOnEscape={ false }
        >
            <div
                style={ {
                    height: "3px",
                    background: "linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)",
                } }
            ></div>
            <Formik
                innerRef={ formRef }
                enableReinitialize={ true }
                initialValues={ initFormState }
                onSubmit={ onSubmit }
            >
                {(formik: FormikProps<TInputForm>) => (
                    <Form onSubmit={ formik.handleSubmit }>
                        <Row className={ "mt-4 md:mb-7 sm:mb-7" }>
                            <Col sm={ 12 }>
                                <Field
                                    inline={ true }
                                    name="consider_all_pages"
                                    component={ InputCheckboxField }
                                    label={ t("consider all pages") }
                                />
                            </Col>

                            <Col sm={ 12 }>
                                <Field inline={ true } name="title" component={ InputCheckboxField } label={ t("title") } />
                            </Col>

                            <Col sm={ 12 }>
                                <Field inline={ true } name="slug" component={ InputCheckboxField } label={ t("slug") } />
                            </Col>

                            <Col sm={ 12 }>
                                <Field
                                    inline={ true }
                                    name="created_at"
                                    component={ InputCheckboxField }
                                    label={ t("created at") }
                                />
                            </Col>
                        </Row>
                        <div className="filter filter-card-footer" style={ { width: "385px" } }>
                            <Button
                                type={ "submit" }
                                disabled={ formik.isSubmitting }
                                tooltip={ t("apply filter") }
                                size={ "small" }
                                outlined
                                tooltipOptions={ { position: "top" } }
                            >
                                <i className="pi pi-filter me-2" />
                                { t("filter") }
                            </Button>
                            <Button
                                type={ "button" }
                                disabled={ formik.isSubmitting }
                                onClick={ onClose }
                                tooltip={ t("operation cancel") }
                                className={ "ms-2" }
                                size={ "small" }
                                severity={ "danger" }
                                outlined
                                tooltipOptions={ { position: "top" } }
                            >
                                <i className="pi pi-arrow-left me-2" />
                                { t("close") }
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </Sidebar>
    );
}

export default withTranslation()(ProviderExcel);
